import { create } from 'zustand';
import { FamilyMember, SharedLink, PersonalInfo, AddRequest, PersonalInfoRequest } from '@/types';
import { generateId, generateToken } from '@/utils/generators';
import { getAppData, setData } from '@/services/local/storage';

interface FamilyState {
  members: FamilyMember[];
  sharedLinks: SharedLink[];
  personalInfos: PersonalInfo[];
  requests: AddRequest[];
  personalInfoRequests: PersonalInfoRequest[];
  
  // Member actions
  addMember: (member: FamilyMember) => void;
  updateMember: (id: string, updates: Partial<FamilyMember>) => void;
  deleteMember: (id: string) => void;
  
  // Shared link actions
  createSharedLink: (type: SharedLink['type'], targetId: string, oneTime?: boolean) => SharedLink;
  updateSharedLink: (id: string, updates: Partial<SharedLink>) => void;
  deleteSharedLink: (id: string) => void;
  getSharedLink: (token: string) => SharedLink | null;
  
  // Request actions
  addRequest: (request: AddRequest) => void;
  addPersonalInfoRequest: (request: PersonalInfoRequest) => void;
  updateRequestStatus: (id: string, status: AddRequest['status']) => void;
  updatePersonalInfoRequestStatus: (id: string, status: PersonalInfoRequest['status']) => void;
  
  // Personal info actions
  addPersonalInfo: (info: PersonalInfo) => void;
  updatePersonalInfo: (id: string, updates: Partial<PersonalInfo>) => void;
  deletePersonalInfo: (id: string) => void;
}

const initialData = getAppData();

export const useFamilyStore = create<FamilyState>((set, get) => ({
  // Initial state
  members: initialData.members || [],
  sharedLinks: initialData.sharedLinks || [],
  personalInfos: initialData.personalInfos || [],
  requests: initialData.requests || [],
  personalInfoRequests: [],

  // Member actions
  addMember: (member) => {
    set(state => {
      const newMembers = [...state.members, member];
      setData('app_data', { ...get(), members: newMembers });
      return { members: newMembers };
    });
  },

  updateMember: (id, updates) => {
    set(state => {
      const newMembers = state.members.map(member =>
        member.id === id ? { ...member, ...updates } : member
      );
      setData('app_data', { ...get(), members: newMembers });
      return { members: newMembers };
    });
  },

  deleteMember: (id) => {
    set(state => {
      const newMembers = state.members.filter(member => member.id !== id);
      setData('app_data', { ...get(), members: newMembers });
      return { members: newMembers };
    });
  },

  // Shared link actions
  createSharedLink: (type, targetId, oneTime = false) => {
    const newLink: SharedLink = {
      id: generateId(),
      type,
      targetId,
      token: generateToken(),
      createdAt: Date.now(),
      isActive: true,
      useCount: 0,
      maxUses: oneTime ? 1 : undefined,
    };

    set(state => {
      const newLinks = [...state.sharedLinks, newLink];
      setData('app_data', { ...get(), sharedLinks: newLinks });
      return { sharedLinks: newLinks };
    });

    return newLink;
  },

  updateSharedLink: (id, updates) => {
    set(state => {
      const newLinks = state.sharedLinks.map(link =>
        link.id === id ? { ...link, ...updates } : link
      );
      setData('app_data', { ...get(), sharedLinks: newLinks });
      return { sharedLinks: newLinks };
    });
  },

  deleteSharedLink: (id) => {
    set(state => {
      const newLinks = state.sharedLinks.filter(link => link.id !== id);
      setData('app_data', { ...get(), sharedLinks: newLinks });
      return { sharedLinks: newLinks };
    });
  },

  getSharedLink: (token) => {
    const link = get().sharedLinks.find(l => l.token === token);
    if (!link || !link.isActive) return null;
    
    if (link.maxUses && link.useCount >= link.maxUses) {
      get().updateSharedLink(link.id, { isActive: false });
      return null;
    }

    return link;
  },

  // Request actions
  addRequest: (request) => {
    set(state => {
      console.log("addRequest:", state);
      const newRequests = [...state.requests, request];
      setData('app_data', { ...get(), requests: newRequests });
      return { requests: newRequests };
    });
  },

  // addPersonalInfoRequest: (request) => {
  //   set(state => {
  //     console.log("addPersonalInfoRequest:", state);
  //     const newRequests = [...state.personalInfoRequests, request];
  //     setData('app_data', { ...get(), personalInfoRequests: newRequests });
  //     return { personalInfoRequests: newRequests };
  //   });
  // },

  addPersonalInfoRequest: (request) => {
    set(state => {
      const newRequests = [...state.personalInfoRequests, request];
      const newState = { ...state, personalInfoRequests: newRequests };
      setData('app_data', newState);
      return { personalInfoRequests: newRequests };
    });
  },

  updateRequestStatus: (id, status) => {
    set(state => {
      const newRequests = state.requests.map(request =>
        request.id === id ? { ...request, status } : request
      );
      setData('app_data', { ...get(), requests: newRequests });
      return { requests: newRequests };
    });
  },

  updatePersonalInfoRequestStatus: (id, status) => {
    set(state => {
      const newRequests = state.personalInfoRequests.map(request =>
        request.id === id ? { ...request, status } : request
      );
      setData('app_data', { ...get(), personalInfoRequests: newRequests });
      return { personalInfoRequests: newRequests };
    });
  },

  // Personal info actions
  addPersonalInfo: (info) => {
    set(state => {
      const newInfos = [...state.personalInfos, info];
      setData('app_data', { ...get(), personalInfos: newInfos });
      return { personalInfos: newInfos };
    });
  },

  updatePersonalInfo: (id, updates) => {
    set(state => {
      const newInfos = state.personalInfos.map(info =>
        info.id === id ? { ...info, ...updates } : info
      );
      setData('app_data', { ...get(), personalInfos: newInfos });
      return { personalInfos: newInfos };
    });
  },

  deletePersonalInfo: (id) => {
    set(state => {
      const newInfos = state.personalInfos.filter(info => info.id !== id);
      setData('app_data', { ...get(), personalInfos: newInfos });
      return { personalInfos: newInfos };
    });
  }
}));